import axiosInstance from '../api/axiosInstance';
import { OJT_ENDPOINTS as EP, OJT_SCALE_ADMIN_ENDPOINTS as SEP, OJT_EVALUATIONS_ENDPOINTS as QEP } from '@/api/endpoints/ojt.endpoints';
import {
  OjtActivity,
  OjtActivityUpdate,
  OjtAttachmentInput,
  OjtQueueItem,
  OjtGradeOverridePayload,
  OjtLearnerSavePayload,
  OjtManagerSavePayload,
  OjtScale,
  OjtScaleInput,
  OjtSmeSavePayload,
  OjtSubmission,
  OjtPerson,
  OjtSubmissionListResponse,
  OjtTask,
  OjtTaskInput,
  OjtView,
} from '@/types/ojt.types';

/** Course "OJT" ACTIVITY API — trainer config + tasks + scales, the
 *  Learner → SME → Manager workflow, grading and reporting. */
export const OjtActivityApi = {
  // ── Config ──────────────────────────────────────────────────────────
  async getOjt(courseId: number, activityId: number): Promise<OjtActivity> {
    const res = await axiosInstance.get<OjtActivity>(EP.OJT(courseId, activityId));
    return res.data;
  },
  async updateOjt(courseId: number, activityId: number, payload: OjtActivityUpdate): Promise<OjtActivity> {
    const res = await axiosInstance.put<OjtActivity>(EP.OJT(courseId, activityId), payload);
    return res.data;
  },

  // ── Tasks ───────────────────────────────────────────────────────────
  async listTasks(courseId: number, activityId: number): Promise<OjtTask[]> {
    const res = await axiosInstance.get<OjtTask[]>(EP.TASKS(courseId, activityId));
    return res.data;
  },
  async createTask(courseId: number, activityId: number, payload: OjtTaskInput): Promise<OjtTask> {
    const res = await axiosInstance.post<OjtTask>(EP.TASKS(courseId, activityId), payload);
    return res.data;
  },
  async updateTask(courseId: number, taskId: number, payload: OjtTaskInput): Promise<OjtTask> {
    const res = await axiosInstance.put<OjtTask>(EP.TASK(courseId, taskId), payload);
    return res.data;
  },
  async deleteTask(courseId: number, taskId: number): Promise<void> {
    await axiosInstance.delete(EP.TASK(courseId, taskId));
  },

  // ── Scales / rubrics ────────────────────────────────────────────────
  async listScales(courseId: number, activityId?: number): Promise<OjtScale[]> {
    const res = await axiosInstance.get<OjtScale[]>(EP.SCALES(courseId), {
      params: activityId ? { activity_id: activityId } : undefined,
    });
    return res.data;
  },
  async createScale(courseId: number, payload: OjtScaleInput): Promise<OjtScale> {
    const res = await axiosInstance.post<OjtScale>(EP.SCALE_CREATE(courseId), payload);
    return res.data;
  },
  async updateScale(courseId: number, scaleId: number, payload: OjtScaleInput): Promise<OjtScale> {
    const res = await axiosInstance.put<OjtScale>(EP.SCALE(courseId, scaleId), payload);
    return res.data;
  },
  async deleteScale(courseId: number, scaleId: number): Promise<void> {
    await axiosInstance.delete(EP.SCALE(courseId, scaleId));
  },

  // ── Submissions + report ────────────────────────────────────────────
  async listSubmissions(courseId: number, activityId: number, status?: string): Promise<OjtSubmissionListResponse> {
    const res = await axiosInstance.get<OjtSubmissionListResponse>(EP.SUBMISSIONS(courseId, activityId), {
      params: status ? { status } : undefined,
    });
    return res.data;
  },
  async report(courseId: number, activityId: number): Promise<OjtSubmissionListResponse & { summary?: any }> {
    const res = await axiosInstance.get(EP.REPORT(courseId, activityId));
    return res.data;
  },
  async getSubmission(courseId: number, submissionId: number): Promise<OjtSubmission> {
    const res = await axiosInstance.get<OjtSubmission>(EP.SUBMISSION(courseId, submissionId));
    return res.data;
  },
  async saveSme(courseId: number, submissionId: number, payload: OjtSmeSavePayload): Promise<OjtSubmission> {
    const res = await axiosInstance.put<OjtSubmission>(EP.SME(courseId, submissionId), payload);
    return res.data;
  },
  async saveManager(courseId: number, submissionId: number, payload: OjtManagerSavePayload): Promise<OjtSubmission> {
    const res = await axiosInstance.put<OjtSubmission>(EP.MANAGER(courseId, submissionId), payload);
    return res.data;
  },
  async overrideGrade(courseId: number, submissionId: number, payload: OjtGradeOverridePayload): Promise<OjtSubmission> {
    const res = await axiosInstance.put<OjtSubmission>(EP.GRADE(courseId, submissionId), payload);
    return res.data;
  },
  /** Named supervisors of the activity, and the course's Subject Matter Experts (used when none are named). */
  async supervisors(courseId: number, activityId: number): Promise<{ supervisors: OjtPerson[]; course_smes: OjtPerson[] }> {
    const res = await axiosInstance.get(EP.SUPERVISORS(courseId, activityId));
    return res.data;
  },
  async supervisorCandidates(courseId: number, activityId: number, q: string): Promise<OjtPerson[]> {
    const res = await axiosInstance.get<OjtPerson[]>(EP.SUPERVISOR_CANDIDATES(courseId, activityId), { params: { q } });
    return res.data;
  },
  /** Send it back a step with a note: supervisor → learner, manager → supervisor. */
  async returnSubmission(courseId: number, submissionId: number, note: string): Promise<OjtSubmission> {
    const res = await axiosInstance.put<OjtSubmission>(EP.RETURN(courseId, submissionId), { note });
    return res.data;
  },
  async reopen(courseId: number, submissionId: number, note?: string): Promise<OjtSubmission> {
    const res = await axiosInstance.put<OjtSubmission>(EP.REOPEN(courseId, submissionId), { note });
    return res.data;
  },

  // ── Learner / participant ───────────────────────────────────────────
  async myOjt(courseId: number, activityId: number): Promise<OjtView> {
    const res = await axiosInstance.get<OjtView>(EP.MY(courseId, activityId));
    return res.data;
  },
  async submissionView(courseId: number, activityId: number, submissionId: number): Promise<OjtView> {
    const res = await axiosInstance.get<OjtView>(EP.SUBMISSION_VIEW(courseId, activityId, submissionId));
    return res.data;
  },
  async saveLearner(courseId: number, submissionId: number, payload: OjtLearnerSavePayload): Promise<OjtSubmission> {
    const res = await axiosInstance.put<OjtSubmission>(EP.LEARNER(courseId, submissionId), payload);
    return res.data;
  },
  async addAttachment(courseId: number, submissionId: number, payload: OjtAttachmentInput): Promise<OjtSubmission> {
    const res = await axiosInstance.post<OjtSubmission>(EP.ATTACHMENT(courseId, submissionId), payload);
    return res.data;
  },
  async deleteAttachment(courseId: number, attachmentId: number): Promise<void> {
    await axiosInstance.delete(EP.ATTACHMENT_DELETE(courseId, attachmentId));
  },
};

/** Site-level (tenant-wide) reusable OJT rubric/scale admin API
 *  (Settings → Learning → OJT Rubric Scales). */
export const OjtScaleAdminApi = {
  async list(): Promise<OjtScale[]> {
    const res = await axiosInstance.get<OjtScale[]>(SEP.LIST);
    return res.data;
  },
  async create(payload: OjtScaleInput): Promise<OjtScale> {
    const res = await axiosInstance.post<OjtScale>(SEP.CREATE, payload);
    return res.data;
  },
  async update(scaleId: number, payload: OjtScaleInput): Promise<OjtScale> {
    const res = await axiosInstance.put<OjtScale>(SEP.ONE(scaleId), payload);
    return res.data;
  },
  async remove(scaleId: number): Promise<void> {
    await axiosInstance.delete(SEP.ONE(scaleId));
  },
};

/** SME cross-course evaluation inbox (the "My OJT evaluations" page + nav badge). */
/** Fired when an OJT record changes hands (submitted, evaluated, returned, signed off), so the
 *  "OJT evaluations" badge, the dashboard count and the evaluations list re-count. */
const OJT_QUEUE_CHANGED = 'ilearn:ojt-queue-changed';
export const announceOjtQueueChanged = (): void => { window.dispatchEvent(new Event(OJT_QUEUE_CHANGED)); };
/** Run ``refresh`` when a record changes hands here or the window regains focus (another tab). */
export const onOjtQueueChange = (refresh: () => void): (() => void) => {
  window.addEventListener(OJT_QUEUE_CHANGED, refresh);
  window.addEventListener('focus', refresh);
  return () => {
    window.removeEventListener(OJT_QUEUE_CHANGED, refresh);
    window.removeEventListener('focus', refresh);
  };
};

export const OjtEvaluationsApi = {
  async queue(): Promise<OjtQueueItem[]> {
    const res = await axiosInstance.get<OjtQueueItem[]>(QEP.QUEUE);
    return res.data;
  },
  /** Whether I evaluate any OJT (course Subject Matter Expert or a named supervisor). */
  /** Every record I evaluate (waiting for me, with the learner, at sign-off, recently completed). */
  async records(): Promise<OjtQueueItem[]> {
    const res = await axiosInstance.get<OjtQueueItem[]>(QEP.RECORDS);
    return res.data;
  },

  async amEvaluator(): Promise<boolean> {
    const res = await axiosInstance.get<{ is_evaluator: boolean }>(QEP.AM_EVALUATOR);
    return !!res.data?.is_evaluator;
  },
};
